import type { InputMethod, NavigationDirection } from "../../input";
import { getAdjacentId } from "../../input";
import {
  completeChoiceMatchProgress,
  createChoiceMatchProgressState,
  createNeutralRetryProgress,
  requestPromptReplay,
  type ChoiceMatchProgressState
} from "./progress";
import type { MatchingPair, MatchingPayload } from "./schemas";

export type MatchingSide = "left" | "right";

export interface MatchingItemReference {
  readonly side: MatchingSide;
  readonly itemId: string;
}

export type MatchingItemKey = `${MatchingSide}:${string}`;

export interface MatchingActivityState extends ChoiceMatchProgressState {
  readonly focusedItemKey: MatchingItemKey | null;
  readonly selectedItem: MatchingItemReference | null;
  readonly matchedPairs: readonly MatchingPair[];
  readonly lastInputMethod: InputMethod | null;
}

export type MatchingActivityAction =
  | {
      readonly type: "move-focus";
      readonly direction: NavigationDirection;
    }
  | {
      readonly type: "select-item";
      readonly item: MatchingItemReference;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "replay-prompt" };

export function createMatchingItemKey(
  item: MatchingItemReference
): MatchingItemKey {
  return `${item.side}:${item.itemId}`;
}

function parseMatchingItemKey(
  itemKey: MatchingItemKey
): MatchingItemReference | null {
  const separatorIndex = itemKey.indexOf(":");
  const side = itemKey.slice(0, separatorIndex);
  const itemId = itemKey.slice(separatorIndex + 1);

  if ((side !== "left" && side !== "right") || itemId.length === 0) {
    return null;
  }

  return { side, itemId };
}

function isItemMatched(
  matchedPairs: readonly MatchingPair[],
  item: MatchingItemReference
): boolean {
  return matchedPairs.some((pair) =>
    item.side === "left"
      ? pair.leftId === item.itemId
      : pair.rightId === item.itemId
  );
}

export function getMatchingKeyboardOrder(
  payload: MatchingPayload,
  matchedPairs: readonly MatchingPair[] = []
): readonly MatchingItemKey[] {
  const orderedKeys: MatchingItemKey[] = [];

  for (let index = 0; index < payload.leftItems.length; index += 1) {
    const leftItem = payload.leftItems[index];
    const rightItem = payload.rightItems[index];

    if (leftItem !== undefined) {
      const item = { side: "left", itemId: leftItem.id } as const;

      if (!isItemMatched(matchedPairs, item)) {
        orderedKeys.push(createMatchingItemKey(item));
      }
    }

    if (rightItem !== undefined) {
      const item = { side: "right", itemId: rightItem.id } as const;

      if (!isItemMatched(matchedPairs, item)) {
        orderedKeys.push(createMatchingItemKey(item));
      }
    }
  }

  return orderedKeys;
}

export function createMatchingActivityState(): MatchingActivityState {
  return {
    ...createChoiceMatchProgressState(),
    focusedItemKey: null,
    selectedItem: null,
    matchedPairs: [],
    lastInputMethod: null
  };
}

function isKnownItem(
  payload: MatchingPayload,
  item: MatchingItemReference
): boolean {
  const items = item.side === "left" ? payload.leftItems : payload.rightItems;
  return items.some(({ id }) => id === item.itemId);
}

function areSameItem(
  left: MatchingItemReference,
  right: MatchingItemReference
): boolean {
  return left.side === right.side && left.itemId === right.itemId;
}

function findPair(
  payload: MatchingPayload,
  first: MatchingItemReference,
  second: MatchingItemReference
): MatchingPair | undefined {
  const leftId = first.side === "left" ? first.itemId : second.itemId;
  const rightId = first.side === "right" ? first.itemId : second.itemId;

  return payload.pairs.find(
    (pair) => pair.leftId === leftId && pair.rightId === rightId
  );
}

function matchingPairKey(pair: MatchingPair): string {
  return `${pair.leftId}:${pair.rightId}`;
}

function addMatchedPair(
  payload: MatchingPayload,
  matchedPairs: readonly MatchingPair[],
  pair: MatchingPair
): readonly MatchingPair[] {
  const matchedPairKeys = new Set(matchedPairs.map(matchingPairKey));
  matchedPairKeys.add(matchingPairKey(pair));

  return payload.pairs.filter((candidate) =>
    matchedPairKeys.has(matchingPairKey(candidate))
  );
}

function selectMatchingItem(
  payload: MatchingPayload,
  state: MatchingActivityState,
  item: MatchingItemReference,
  inputMethod: InputMethod
): MatchingActivityState {
  if (
    state.status === "complete" ||
    !isKnownItem(payload, item) ||
    isItemMatched(state.matchedPairs, item)
  ) {
    return state;
  }

  const focusedItemKey = createMatchingItemKey(item);

  if (state.selectedItem === null) {
    return {
      ...state,
      feedback: null,
      focusedItemKey,
      selectedItem: { ...item },
      lastInputMethod: inputMethod
    };
  }

  if (areSameItem(state.selectedItem, item)) {
    return {
      ...state,
      feedback: null,
      focusedItemKey,
      selectedItem: null,
      lastInputMethod: inputMethod
    };
  }

  if (state.selectedItem.side === item.side) {
    return {
      ...state,
      feedback: null,
      focusedItemKey,
      selectedItem: { ...item },
      lastInputMethod: inputMethod
    };
  }

  const pair = findPair(payload, state.selectedItem, item);

  if (pair === undefined) {
    return {
      ...state,
      ...createNeutralRetryProgress(state),
      focusedItemKey,
      selectedItem: null,
      lastInputMethod: inputMethod
    };
  }

  const matchedPairs = addMatchedPair(payload, state.matchedPairs, pair);
  const completed = matchedPairs.length === payload.pairs.length;

  return {
    ...state,
    ...(completed
      ? completeChoiceMatchProgress(state, payload.pairs.length)
      : { feedback: null }),
    focusedItemKey: null,
    selectedItem: null,
    matchedPairs,
    lastInputMethod: inputMethod
  };
}

export function reduceMatchingActivity(
  payload: MatchingPayload,
  state: MatchingActivityState,
  action: MatchingActivityAction
): MatchingActivityState {
  switch (action.type) {
    case "move-focus": {
      if (state.status === "complete") {
        return state;
      }

      const focusedItemKey = getAdjacentId(
        getMatchingKeyboardOrder(payload, state.matchedPairs),
        state.focusedItemKey,
        action.direction
      );

      if (focusedItemKey === null) {
        return state;
      }

      return {
        ...state,
        focusedItemKey,
        lastInputMethod: "keyboard"
      };
    }
    case "select-item":
      return selectMatchingItem(
        payload,
        state,
        action.item,
        action.inputMethod
      );
    case "activate-focused": {
      if (state.focusedItemKey === null) {
        return state;
      }

      const item = parseMatchingItemKey(state.focusedItemKey);
      return item === null
        ? state
        : selectMatchingItem(payload, state, item, "keyboard");
    }
    case "replay-prompt":
      return {
        ...state,
        ...requestPromptReplay(state)
      };
  }
}
